/**
 * ResolutionFunnel — Wave 6.2 RESOLUTION-METRICS-KPI
 *
 * Stacked-bar chart: Origin × Resolution code.  Surfaces:
 *   • lobby-queue  → Processed conversion ratio
 *   • rcp-portal   → Withdrawn dropoff
 *   • fsr-self     → Cancelled volume
 *
 * Uses no chart library — inline SVG.  Click a segment to drill into the
 * filtered cart-list at /metrics/carts?range=…&originRole=…&resolution=…
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { CartResolutionByOriginRow, CartResolutionRange } from '@retail-ops/shared-types';

interface ResolutionFunnelProps {
  range: CartResolutionRange;
  rows: ReadonlyArray<CartResolutionByOriginRow>;
  branchFilter?: string;
}

const SEGMENTS = [
  { key: 'processed', label: 'Processed', color: 'var(--pcb-green)', tone: 'green' as const },
  { key: 'withdrawn', label: 'Withdrawn', color: 'var(--pcb-amber-text)', tone: 'amber' as const },
  { key: 'adverseAction', label: 'Adverse-Action', color: 'var(--pcb-red)', tone: 'red' as const },
  { key: 'cancelled', label: 'Cancelled', color: 'var(--ink-500)', tone: 'default' as const },
  { key: 'open', label: 'Open', color: 'var(--pcb-blue)', tone: 'default' as const },
] as const;

const ROW_HEIGHT = 28;
const ROW_GAP = 12;
const LEFT_GUTTER = 140;

function humanizeOrigin(role: string): string {
  switch (role) {
    case 'fsr-self': return 'FSR self-serve';
    case 'lobby-queue': return 'Lobby queue';
    case 'lobby-entry-walkin': return 'Lobby — walk-in';
    case 'lobby-entry-appointment': return 'Lobby — appt';
    case 'kiosk': return 'Kiosk';
    case 'rcp-portal': return 'Customer portal';
    default:
      return role.charAt(0).toUpperCase() + role.slice(1).replace(/-/g, ' ');
  }
}

export function ResolutionFunnel({ range, rows, branchFilter }: ResolutionFunnelProps): JSX.Element {
  const navigate = useNavigate();
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.total - a.total),
    [rows],
  );

  const maxTotal = useMemo(
    () => sorted.reduce((m, r) => (r.total > m ? r.total : m), 0),
    [sorted],
  );

  if (sorted.length === 0) {
    return (
      <div style={{ padding: 'var(--sp-7)', fontSize: 12, color: 'var(--ink-600)' }}>
        No carts in this window. Try a wider range.
      </div>
    );
  }

  const height = sorted.length * (ROW_HEIGHT + ROW_GAP);

  function drill(originRole: string, resolution?: string): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('originRole', originRole);
    if (resolution) params.set('resolution', resolution);
    if (branchFilter && branchFilter !== 'all') params.set('branch', branchFilter);
    navigate(`/metrics/carts?${params.toString()}`);
  }

  return (
    <div className="resolution-funnel">
      <svg
        viewBox={`0 0 800 ${height}`}
        preserveAspectRatio="xMinYMin meet"
        width="100%"
        height={height}
        role="img"
        aria-label="Resolution funnel by origin"
      >
        {sorted.map((row, i) => {
          const y = i * (ROW_HEIGHT + ROW_GAP);
          const widthScale = maxTotal > 0 ? (800 - LEFT_GUTTER - 60) / maxTotal : 0;
          let xCursor = LEFT_GUTTER;
          return (
            <g key={row.originRole}>
              <text
                x={LEFT_GUTTER - 8}
                y={y + ROW_HEIGHT / 2 + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--ink-700)"
                style={{ fontWeight: 600 }}
              >
                {humanizeOrigin(row.originRole)}
              </text>
              {SEGMENTS.map((seg) => {
                const v = (row as unknown as Record<string, number>)[seg.key] ?? 0;
                if (v === 0) return null;
                const w = v * widthScale;
                const xPos = xCursor;
                xCursor += w;
                return (
                  <rect
                    key={seg.key}
                    x={xPos}
                    y={y}
                    width={Math.max(0, w)}
                    height={ROW_HEIGHT}
                    fill={seg.color}
                    style={{ cursor: 'pointer' }}
                    onClick={() => drill(row.originRole, seg.label)}
                  >
                    <title>{`${humanizeOrigin(row.originRole)} · ${seg.label}: ${v}`}</title>
                  </rect>
                );
              })}
              <text
                x={xCursor + 6}
                y={y + ROW_HEIGHT / 2 + 4}
                fontSize={11}
                fill="var(--ink-900)"
                style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}
              >
                {row.total}
              </text>
            </g>
          );
        })}
      </svg>
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-5)',
          flexWrap: 'wrap',
          fontSize: 11,
          color: 'var(--ink-700)',
          marginTop: 'var(--sp-5)',
        }}
        aria-label="Legend"
      >
        {SEGMENTS.map((seg) => (
          <span key={seg.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: seg.color,
                display: 'inline-block',
              }}
            />
            {seg.label}
          </span>
        ))}
      </div>
    </div>
  );
}
